// Cómo se llaman en pantalla las cifras que este módulo sabe medir.
//
// Aparte del resto de los textos por un motivo que no es de tamaño: estas claves
// son un CONTRATO con el catálogo de analítica (`backend/analytics.js`), no copia
// de una pantalla. Cada `labelKey` que aquel fichero declara tiene aquí su
// traducción en los cuatro idiomas, y una que falte no rompe nada — sale el id
// hecho legible, en inglés, en medio de una pantalla en castellano.
//
// La ayuda de un dataset (`help`) es lo que alguien lee en el desplegable de
// Explorar antes de elegirlo. Dice qué es UNA FILA, que es la pregunta que de
// verdad distingue dos conjuntos de datos parecidos.

const analytics = {
  es: {
    quotes: {
      label: 'Presupuestos',
      help: 'Una fila por presupuesto, borradores incluidos. Los archivados quedan fuera',
    },
    dates: {
      created: 'Fecha de creación',
      sent: 'Fecha de envío',
      decided: 'Fecha de respuesta',
      validUntil: 'Válido hasta',
    },
    metrics: {
      quotes: 'Presupuestos',
      value: 'Importe presupuestado',
      sent: 'Enviados',
      accepted: 'Ganados',
      rejected: 'Perdidos',
      open: 'Sin respuesta',
      acceptedValue: 'Importe ganado',
      openValue: 'Importe en juego',
      rejectedValue: 'Importe perdido',
      winRate: 'Tasa de acierto',
      winRateByValue: 'Tasa de acierto en dinero',
      average: 'Presupuesto medio',
      daysToDecide: 'Días hasta la respuesta',
      discount: 'Descuento concedido',
      cost: 'Coste presupuestado',
      margin: 'Margen previsto',
    },
    dimensions: {
      customer: 'Cliente',
      project: 'Obra',
      status: 'Estado',
      lossReason: 'Motivo de la pérdida',
      author: 'Comercial',
      currency: 'Moneda',
    },
    filters: {
      total: 'Total del presupuesto',
    },
  },
  ca: {
    quotes: {
      label: 'Pressupostos',
      help: 'Una fila per pressupost, esborranys inclosos. Els arxivats queden fora',
    },
    dates: {
      created: 'Data de creació',
      sent: 'Data d\'enviament',
      decided: 'Data de resposta',
      validUntil: 'Vàlid fins',
    },
    metrics: {
      quotes: 'Pressupostos',
      value: 'Import pressupostat',
      sent: 'Enviats',
      accepted: 'Guanyats',
      rejected: 'Perduts',
      open: 'Sense resposta',
      acceptedValue: 'Import guanyat',
      openValue: 'Import en joc',
      rejectedValue: 'Import perdut',
      winRate: 'Taxa d\'encert',
      winRateByValue: 'Taxa d\'encert en diners',
      average: 'Pressupost mitjà',
      daysToDecide: 'Dies fins a la resposta',
      discount: 'Descompte concedit',
      cost: 'Cost pressupostat',
      margin: 'Marge previst',
    },
    dimensions: {
      customer: 'Client',
      project: 'Obra',
      status: 'Estat',
      lossReason: 'Motiu de la pèrdua',
      author: 'Comercial',
      currency: 'Moneda',
    },
    filters: {
      total: 'Total del pressupost',
    },
  },
  en: {
    quotes: {
      label: 'Quotes',
      help: 'One row per quote, drafts included. Archived ones are excluded',
    },
    dates: {
      created: 'Created',
      sent: 'Sent',
      decided: 'Answered',
      validUntil: 'Valid until',
    },
    metrics: {
      quotes: 'Quotes',
      value: 'Quoted value',
      sent: 'Sent',
      accepted: 'Won',
      rejected: 'Lost',
      open: 'Open',
      acceptedValue: 'Won value',
      openValue: 'Value at stake',
      rejectedValue: 'Lost value',
      winRate: 'Win rate',
      winRateByValue: 'Win rate by value',
      average: 'Average quote',
      daysToDecide: 'Days to decide',
      discount: 'Discount given',
      cost: 'Quoted cost',
      margin: 'Expected margin',
    },
    dimensions: {
      customer: 'Customer',
      project: 'Job',
      status: 'Status',
      lossReason: 'Loss reason',
      author: 'Author',
      currency: 'Currency',
    },
    filters: {
      total: 'Quote total',
    },
  },
  fr: {
    quotes: {
      label: 'Devis',
      help: 'Une ligne par devis, brouillons compris. Les archivés sont exclus',
    },
    dates: {
      created: 'Date de création',
      sent: 'Date d\'envoi',
      decided: 'Date de réponse',
      validUntil: 'Valable jusqu\'au',
    },
    metrics: {
      quotes: 'Devis',
      value: 'Montant proposé',
      sent: 'Envoyés',
      accepted: 'Gagnés',
      rejected: 'Perdus',
      open: 'Sans réponse',
      acceptedValue: 'Montant gagné',
      openValue: 'Montant en jeu',
      rejectedValue: 'Montant perdu',
      winRate: 'Taux de réussite',
      winRateByValue: 'Taux de réussite en montant',
      average: 'Devis moyen',
      daysToDecide: 'Jours jusqu\'à la réponse',
      discount: 'Remise accordée',
      cost: 'Coût prévu',
      margin: 'Marge prévue',
    },
    dimensions: {
      customer: 'Client',
      project: 'Chantier',
      status: 'Statut',
      lossReason: 'Raison de la perte',
      author: 'Commercial',
      currency: 'Devise',
    },
    filters: {
      total: 'Total du devis',
    },
  },
}

/**
 * Adds the analytics vocabulary to the module's own catalogue, locale by locale.
 *
 * Merged here rather than pasted into the big literal so the block stays
 * readable and so adding a metric is one line in two files instead of four
 * edits in the middle of eight hundred.
 */
export function withAnalytics(base, namespace) {
  const merged = {}
  for (const [locale, catalogue] of Object.entries(base)) {
    merged[locale] = {
      ...catalogue,
      [namespace]: { ...catalogue[namespace], analytics: analytics[locale] },
    }
  }
  return merged
}

export default analytics
